/**
 * API服务
 * 封装所有后端API调用
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { message } from 'antd'

// API基础URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_INFO_KEY = 'user_info'

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean }

let refreshPromise: Promise<string> | null = null

const clearAuthStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_INFO_KEY)
}

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) {
    throw new Error('缺少刷新令牌')
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 30000 })
      .then((response) => {
        const payload = response.data?.data
        if (!response.data?.success || !payload?.access_token || !payload?.refresh_token) {
          throw new Error('刷新令牌响应无效')
        }

        localStorage.setItem(ACCESS_TOKEN_KEY, payload.access_token)
        localStorage.setItem(REFRESH_TOKEN_KEY, payload.refresh_token)
        return payload.access_token as string
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem(ACCESS_TOKEN_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data
  },
  async (error) => {
    // 处理错误
    if (error.response) {
      const { status, data, config } = error.response
      const isLoginRequest = config.url?.includes('/auth/login')
      const isRefreshRequest = config.url?.includes('/auth/refresh')
      // 弹窗内的请求：由调用方展示错误，避免被全局跳转打断
      const isModalDriven = config.url?.includes('/tools/analyze-text')

      switch (status) {
        case 401:
          if (!isLoginRequest && !isRefreshRequest && !(config as RetryableRequestConfig)._retry) {
            try {
              const retryConfig = config as RetryableRequestConfig
              retryConfig._retry = true
              const accessToken = await refreshAccessToken()
              retryConfig.headers = retryConfig.headers || {}
              retryConfig.headers.Authorization = `Bearer ${accessToken}`
              return apiClient(retryConfig)
            } catch {
              // 刷新令牌已失效，按原有的 401 流程退出登录。
            }
          }

          if (isLoginRequest) {
            // 登录请求的401错误，不自动显示消息，让登录页面处理
          } else if (isModalDriven) {
            // 弹窗流程：仅清除 token，交给弹窗提示
            clearAuthStorage()
          } else {
            // 其他请求的401错误，清除token并跳转到登录页
            clearAuthStorage()
            window.location.href = '/login'
            message.error('登录已过期，请重新登录')
          }
          break
        case 403:
          message.error('没有权限访问')
          break
        case 404:
          message.error('请求的资源不存在')
          break
        case 500:
          message.error('服务器错误')
          break
        default:
          if (!isLoginRequest) {
            message.error(data.message || '请求失败')
          }
      }
    } else if (error.request) {
      message.error('网络错误，请检查网络连接')
    } else {
      message.error('请求配置错误')
    }

    return Promise.reject(error)
  }
)

// API接口定义
export const api = {
  // 认证相关
  auth: {
    login: (credentials: { email: string; password: string }) =>
      apiClient.post('/auth/login', credentials),
    
    register: (userData: { username: string; email: string; password: string }) =>
      apiClient.post('/auth/register', userData),
    
    logout: () =>
      apiClient.post('/auth/logout'),
    
    refreshToken: (refreshToken: string) =>
      apiClient.post('/auth/refresh', { refresh_token: refreshToken }),
  },

  // 文章相关
  articles: {
    list: (params?: {
      page?: number
      page_size?: number
      search?: string
      tag?: string
      tag_id?: number
      folder_id?: number
      sort_by?: string
      order?: string
      is_featured?: boolean
      is_read?: boolean
      is_archived?: boolean
    }) =>
      apiClient.get('/articles/', { params }),

    get: (id: number) =>
      apiClient.get(`/articles/${id}`),

    history: (params?: { page?: number; page_size?: number }) =>
      apiClient.get('/articles/history', { params }),

    create: (articleData: {
      url: string
      title?: string
      content?: string
      summary?: string
      tags?: string[]
      folder_id?: number
    }) =>
      apiClient.post('/articles/', articleData),
    
    update: (id: number, articleData: Partial<{
      title: string
      content: string
      summary: string
      tags: string[]
      folder_id: number
      is_archived: boolean
      is_featured: boolean
    }>) =>
      apiClient.put(`/articles/${id}`, articleData),
    
    delete: (id: number) =>
      apiClient.delete(`/articles/${id}`),

    extractUrl: (url: string) =>
      apiClient.post('/articles/extract-url', { url }),
    
    search: (query: string, params?: {
      page?: number
      page_size?: number
      search_type?: 'keyword' | 'semantic'
    }) =>
      apiClient.get('/articles/search', { params: { q: query, ...params } }),

    // 批量操作
    batchDelete: (articleIds: number[]) =>
      apiClient.post('/batch/delete', { article_ids: articleIds }),

    batchAddTags: (articleIds: number[], tagIds: number[]) =>
      apiClient.post('/batch/add-tags', { article_ids: articleIds, tag_ids: tagIds }),

    batchAddTagsByNames: (articleIds: number[], tagNames: string[]) =>
      apiClient.post('/batch/add-tags-by-names', { article_ids: articleIds, tag_names: tagNames }),

    batchRemoveTags: (articleIds: number[], tagIds: number[]) =>
      apiClient.post('/batch/remove-tags', { article_ids: articleIds, tag_ids: tagIds }),

    batchMoveToFolder: (articleIds: number[], folderId: number) =>
      apiClient.post('/batch/move-to-folder', { article_ids: articleIds, folder_id: folderId }),

    batchArchive: (articleIds: number[]) =>
      apiClient.post('/batch/set-archived', { article_ids: articleIds, status: true }),

    batchUnarchive: (articleIds: number[]) =>
      apiClient.post('/batch/set-archived', { article_ids: articleIds, status: false }),

    batchSetRead: (articleIds: number[]) =>
      apiClient.post('/batch/set-read', { article_ids: articleIds, status: true }),

    batchSetUnread: (articleIds: number[]) =>
      apiClient.post('/batch/set-read', { article_ids: articleIds, status: false }),

    batchSetFavorite: (articleIds: number[]) =>
      apiClient.post('/batch/set-favorite', { article_ids: articleIds, status: true }),

    batchUnsetFavorite: (articleIds: number[]) =>
      apiClient.post('/batch/set-favorite', { article_ids: articleIds, status: false }),

    batchSetFeatured: (articleIds: number[]) =>
      apiClient.post('/batch/set-featured', { article_ids: articleIds, status: true }),

    batchUnsetFeatured: (articleIds: number[]) =>
      apiClient.post('/batch/set-featured', { article_ids: articleIds, status: false }),
  },

  // 工具库相关
  tools: {
    list: (params?: {
      page?: number
      size?: number
      q?: string
      archived?: boolean
    }) =>
      apiClient.get('/tools/', { params }),

    search: (params: {
      q: string
      search_type?: 'keyword' | 'semantic' | 'hybrid'
      limit?: number
    }) =>
      apiClient.get('/tools/search', { params }),

    get: (id: number) =>
      apiClient.get(`/tools/${id}`),

   fromArticle: (articleId: number, forceReanalyze = false) =>
     apiClient.post(`/tools/from-article/${articleId}`, {
       force_reanalyze: forceReanalyze
     }),

   reanalyze: (toolId: number) =>
     apiClient.post(`/tools/${toolId}/analyze`),

    analyzeText: (data: { text: string; save?: boolean }) =>
      apiClient.post('/tools/analyze-text', data),

    create: (data: {
      name: string
      description?: string
      tags?: string[]
      links?: Array<{ url: string; type?: string; description?: string }>
      source_article_id?: number
    }) =>
      apiClient.post('/tools/', data),

    update: (id: number, data: Partial<{
      name: string
      description: string
      tags: string[]
      links: Array<{ url: string; type?: string; description?: string }>
      is_favorite: boolean
      is_archived: boolean
    }>) =>
      apiClient.put(`/tools/${id}`, data),

   delete: (id: number) =>
     apiClient.delete(`/tools/${id}`),
  },

  // 标签相关
  tags: {
    list: (params?: { page?: number; page_size?: number }) =>
      apiClient.get('/tags/', { params }),
    
    get: (id: number) =>
      apiClient.get(`/tags/${id}`),
    
    create: (tagData: { name: string; color?: string; description?: string }) =>
      apiClient.post('/tags/', tagData),
    
    update: (id: number, tagData: Partial<{ name: string; color: string; description: string }>) =>
      apiClient.put(`/tags/${id}`, tagData),
    
    delete: (id: number) =>
      apiClient.delete(`/tags/${id}`),
  },

  // 文件夹相关
  folders: {
    list: (params?: { page?: number; page_size?: number }) =>
      apiClient.get('/folders/', { params }),
    
    get: (id: number) =>
      apiClient.get(`/folders/${id}`),
    
    create: (folderData: { name: string; parent_id?: number; description?: string }) =>
      apiClient.post('/folders/', folderData),
    
    update: (id: number, folderData: Partial<{ name: string; parent_id: number; description: string }>) =>
      apiClient.put(`/folders/${id}`, folderData),
    
    delete: (id: number) =>
      apiClient.delete(`/folders/${id}`),
  },

  // 用户相关
  users: {
    me: () =>
      apiClient.get('/users/me'),
    
    update: (userData: Partial<{ username: string; email: string; avatar: string }>) =>
      apiClient.put('/users/me', userData),
    
    changePassword: (passwordData: { old_password: string; new_password: string }) =>
      apiClient.post('/users/me/password', passwordData),
  },

  // 搜索相关
  search: {
    articles: (params: {
      q: string
      search_type?: 'keyword' | 'semantic' | 'hybrid'
      page?: number
      page_size?: number
      tag?: string
      tags?: string
      folder_id?: number
      start_date?: string
      end_date?: string
      sort_by?: string
      order?: 'asc' | 'desc'
      limit?: number
      similarity_threshold?: number
      semantic_weight?: number
      keyword_weight?: number
    }) => {
      const { search_type = 'keyword', ...otherParams } = params

      // 根据搜索类型选择不同的端点
      if (search_type === 'semantic') {
        // 语义搜索端点
        const semanticParams = {
          q: params.q,
          limit: params.page_size || 20,
          similarity_threshold: params.similarity_threshold || 0.7,
          tag_ids: params.tags,
          folder_ids: params.folder_id ? String(params.folder_id) : undefined
        }
        return apiClient.get('/articles/semantic-search', { params: semanticParams })
      } else if (search_type === 'hybrid') {
        // 混合搜索端点
        const hybridParams = {
          q: params.q,
          limit: params.page_size || 20,
          semantic_weight: params.semantic_weight || 0.6,
          keyword_weight: params.keyword_weight || 0.4,
          tag_ids: params.tags,
          folder_ids: params.folder_id ? String(params.folder_id) : undefined
        }
        return apiClient.get('/articles/hybrid-search', { params: hybridParams })
      } else {
        // 关键词搜索端点（原有逻辑）
        return apiClient.get('/articles/search', { params: otherParams })
      }
    },

    suggestions: (q: string) =>
      apiClient.get('/search/suggestions', { params: { q } }),
  },

  // 推荐相关
  recommendations: {
    articles: (params?: { limit?: number }) =>
      apiClient.get('/recommendations/articles', { params }),

    tags: (params?: { limit?: number }) =>
      apiClient.get('/recommendations/tags', { params }),

    similar: (articleId: number, params?: { limit?: number }) =>
      apiClient.get(`/recommendations/similar/${articleId}`, { params }),
  },

  // 统计相关
  stats: {
    overview: () =>
      apiClient.get('/stats/overview'),

    articles: (params?: { start_date?: string; end_date?: string }) =>
      apiClient.get('/stats/articles', { params }),

    tags: () =>
      apiClient.get('/stats/tags'),
  },
}

export default apiClient
